import { LangMap, LangMapEcomm } from "@model/CoursesClass";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import * as actions from "@redux-actions/";
import { useSelector } from "@hooks/redux";
import ModalEvolution from "./ModalEvolution";
import TextField from "./TextField";
import Checkbox from "./Checkbox";
import ButtonV2 from "./ButtonV2";
import { useBreakpointBoolean } from "@hooks/createBreakpoint";
import { useForm } from "react-hook-form";
import { selectUserProfile } from "@utility/Api";
import ErrorMessage from "./ErrorMessage";
import { UserProfile } from "@model/User";
import { setLearnerName } from "@redux-actions/";

type ModalCpdCourseProps = {
  show: boolean;
  close: () => void;
  course: any;
};

type CpdCourseFormValues = {
  learnerName: string;
  contactEmail: string;
  gocNumber: string;
};

const ModalCpdCourse = ({ show, close, course }: ModalCpdCourseProps) => {
  const lang: LangMap & LangMapEcomm = useSelector((state) => state.utils.lang);
  const learnerNameFromRedux: string = useSelector((state) => state.user.learnerName);
  const userProfile: UserProfile & { fullName: string } = useSelector(selectUserProfile);
  const { isPhone, isDesktop } = useBreakpointBoolean();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const dispatch = useDispatch();
  const { onCompleteAllFields } = useSelector((state) => ({
    onCompleteAllFields: state.course.onCompleteAllFields,
  }));

  const useFormValues = useForm<CpdCourseFormValues>({
    mode: "onSubmit",
    defaultValues: {
      learnerName:
        learnerNameFromRedux ||
        userProfile?.learnerName ||
        userProfile?.fullName,
      contactEmail: userProfile?.field1,
      gocNumber: userProfile?.licenseId,
    },
  });
  const { register, errors, handleSubmit } = useFormValues;

  const handleSaveProfile = async (data: CpdCourseFormValues) => {
    const nameToUse = data.learnerName;

    const allFieldsNowFilled =
      !!data.learnerName?.trim() &&
      !!data.contactEmail?.trim() &&
      !!data.gocNumber?.trim();

    setIsLoading(true);
    dispatch(setLearnerName(nameToUse));

    try {
      await new Promise<void>((resolve) => {
        dispatch(
          actions.resetPasswordLogged(
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            null,
            nameToUse,
            data.gocNumber,
            data.contactEmail,
            null
          )
        );
        setTimeout(resolve, 500);
      });

      if (allFieldsNowFilled) {
        close();
        onCompleteAllFields();
      }
    } catch (error) {
      console.error("Error while updating user profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <ModalEvolution
      show={show}
      close={close}
      modalClassName="download-link download-link--cpd"
      title={lang?.CPD_CERTIFICATION_TITLE?.toUpperCase()}
      descriptionElement={
        <p style={{ whiteSpace: "pre-line" }}>
          {lang?.CPD_CERTIFICATION_DESCRIPTION}
        </p>
      }
      hideX={true}
      formElement={
        <form
          className="download-link__modal__form"
          onSubmit={handleSubmit(handleSaveProfile)}
        >
          <div className="form-container">
            <div className="form-field">
              <TextField
                type="text"
                name="learnerName"
                mandatory
                ref={register({
                  validate: (value) => value.trim() !== "" || lang.ERROR_REQUIRED,
                })}
                aria-invalid={!!errors.learnerName ? "true" : "false"}
                isError={!!errors.learnerName}
                placeholder={lang?.CPD_NAME}
                labelFixedTop
                showBothPlaceholderAndLabel
                label={lang?.CPD_NAME}
                htmlFor="learnerName"
                id="learnerName"
              />
              <ErrorMessage error={errors.learnerName} />
            </div>

            <div className="form-field">
              <TextField
                type="text"
                name="contactEmail"
                mandatory
                ref={register({
                  required: lang.ERROR_REQUIRED,
                  validate: {
                    notEmpty: (v) => v.trim() !== "" || lang.ERROR_REQUIRED,
                    validEmail: (v) =>
                      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(
                        v.trim()
                      ) || lang.ERROR_REQUIRED,
                  },
                })}
                aria-invalid={!!errors.contactEmail ? "true" : "false"}
                isError={!!errors.contactEmail}
                placeholder={lang?.CPD_EMAIL}
                labelFixedTop
                showBothPlaceholderAndLabel
                label={lang?.CPD_EMAIL}
                htmlFor="contactEmail"
                id="contactEmail"
              />
            <ErrorMessage error={errors.contactEmail} />
            </div>

            <div className="form-field">
              <TextField
                type="text"
                name="gocNumber"
                mandatory
                ref={register({
                  validate: (value) => value.trim() !== "" || lang.ERROR_REQUIRED,
                })}
                aria-invalid={!!errors.gocNumber ? "true" : "false"}
                isError={!!errors.gocNumber}
                placeholder={lang?.CPD_GOC_PLACEHOLDER}
                labelFixedTop
                showBothPlaceholderAndLabel
                label={lang?.CPD_GOC}
                htmlFor="gocNumber"
                id="gocNumber"
              />
              <ErrorMessage error={errors.gocNumber} />
            </div>

            <div className="info-form">
              <span>{lang?.CPD_INFO_TEXT_1}</span>
              <span>{lang?.CPD_INFO_TEXT_2}</span>
            </div>
          </div>
          <div className="ecommerce-alert-modal__footer">
            <ButtonV2
              variant="primary"
              className="download-link--submit"
              small={isDesktop}
              loading={isLoading}
              handleClick={handleSubmit(handleSaveProfile)}
            >
              {lang.CONTINUE}
            </ButtonV2>
            <ButtonV2
              variant={isPhone ? "text-btn-no-arrow" : "tertiary"}
              small={isPhone || isDesktop}
              className="download-link--cancel"
              handleClick={close}
              type="button"
              disabled={isLoading}
            >
              {lang?.ONBOARDINGCOURSE_FORM_CONTACT_CUSTOM_BUTTON_CANCEL}
            </ButtonV2>
          </div>
        </form>
      }
    />
  );
};

export default ModalCpdCourse;
